import React, { Suspense, useCallback, useMemo, useRef, useState } from 'react';
import { useFrame } from '@react-three/fiber';
import * as THREE from 'three';
import { Billboard, Text } from '@react-three/drei';
import BrainMesh from './BrainMesh';
import NeuralWeb from './NeuralWeb';
import { HOTSPOTS } from './anatomy';
import { useSimStore } from '../state/store';
import { viewState, dragView } from './viewState';

const _q = new THREE.Quaternion();
const _n = new THREE.Vector3();
const _p = new THREE.Vector3();

// A single brain exhibit. `clip` enables a coronal cross-section that follows
// the brain's rotation and exposes the internal limbic nodes.
export default function Brain({ index, position = [0, 0, 0], rotation = [0, 0, 0], scale = 1, data, label, clip = false, material = {}, compareRole = null }) {
  const [hover, setHover] = useState(-1);
  const setHoveredRegion = useSimStore((s) => s.setHoveredRegion);
  const setDragging = useSimStore((s) => s.setDragging);
  const setFocus = useSimStore((s) => s.setFocus);
  const compareMode = useSimStore((s) => s.compareMode);
  const compareEvent = compareMode ? compareRole : null;
  const spin = useRef();
  const orient = useRef();
  const outer = useRef();
  const labelRef = useRef();
  const lastWrite = useRef(0);
  const baseScale = useRef(scale);

  // Coronal cut is always on for the centre brain, and also on BOTH compare
  // brains so the limbic reward vs threat cores are exposed side by side.
  const clipEnabled = clip || compareMode;
  const clipPlanes = useMemo(
    () => (clipEnabled ? [new THREE.Plane(new THREE.Vector3(0, 0, -1), 0.22)] : null),
    [clipEnabled]
  );

  useFrame((state) => {
    const r = viewState.rot[index];
    if (spin.current) {
      spin.current.rotation.x = r.x;
      spin.current.rotation.y = r.y;
    }
    // Focus animation: focused brain glides to centre + scales to 200%.
    // Compare mode: brains 0 & 2 glide side-by-side, brain 1 hides.
    const s = useSimStore.getState();
    const focused = s.focusIndex === index;
    const someFocus = s.focusIndex !== null;
    let targetX = position[0];
    let targetScale = scale;
    if (s.compareMode) {
      if (index === 0) { targetX = -1.55; targetScale = scale * 1.35; }
      else if (index === 2) { targetX = 1.55; targetScale = scale * 1.35; }
      else { targetX = position[0]; targetScale = 0.0001; }
    } else if (focused) {
      targetX = 0;
      targetScale = scale * 2;
    } else if (someFocus) {
      targetScale = 0.0001;
    }
    if (outer.current) outer.current.position.x += (targetX - outer.current.position.x) * 0.1;
    // Show the big serif label only in the calm, settled gallery (never during
    // the orbiting intro tour, focus, compare or neuro-tour) so labels stay
    // centered above each brain and never sweep across / overlap each other.
    if (labelRef.current) labelRef.current.visible = !someFocus && !s.compareMode && !s.tourActive;
    if (orient.current) {
      const swell = s.event === 'clutch' ? 1 + 0.07 * (0.5 + 0.5 * Math.sin(state.clock.elapsedTime * 18)) : 1;
      baseScale.current += (targetScale - baseScale.current) * 0.12;
      orient.current.scale.setScalar(baseScale.current * swell);
      orient.current.visible = baseScale.current > 0.01;
    }
    if (clipPlanes && orient.current) {
      let depth = s.sliceDepth;
      if (s.sliceSweep) {
        depth = 0.42 * Math.sin(state.clock.elapsedTime * 0.5);
        // Only the centre brain writes the swept value back to the store so the
        // slider % stays in sync; compare brains read the same depth locally.
        if (index === 1 && Math.abs(depth - lastWrite.current) > 0.02) {
          lastWrite.current = depth;
          s.setSliceDepth(depth);
        }
      }
      orient.current.updateWorldMatrix(true, false);
      orient.current.getWorldQuaternion(_q);
      _n.set(0, 0, -1).applyQuaternion(_q);
      _p.set(0, 0, depth).applyMatrix4(orient.current.matrixWorld);
      clipPlanes[0].setFromNormalAndCoplanarPoint(_n, _p);
    }
  });

  const onDown = useCallback(
    (e) => {
      e.stopPropagation();
      setDragging(true);
      document.body.style.cursor = 'grabbing';
      let lastX = e.nativeEvent.clientX;
      let lastY = e.nativeEvent.clientY;
      const startX = lastX;
      const startY = lastY;
      const move = (ev) => {
        dragView(index, ev.clientX - lastX, ev.clientY - lastY);
        lastX = ev.clientX;
        lastY = ev.clientY;
      };
      const up = (ev) => {
        setDragging(false);
        document.body.style.cursor = 'auto';
        window.removeEventListener('pointermove', move);
        window.removeEventListener('pointerup', up);
        const moved = Math.abs(ev.clientX - startX) + Math.abs(ev.clientY - startY);
        if (moved < 6) {
          const st = useSimStore.getState();
          st.setFocus(st.focusIndex === index ? null : index);
        }
      };
      window.addEventListener('pointermove', move);
      window.addEventListener('pointerup', up);
    },
    [index, setDragging]
  );

  return (
    <group position={position} ref={outer}>
      <group ref={spin}>
        <group ref={orient} rotation={rotation} scale={scale}>
          {/* invisible drag surface — spin the exhibit by click-dragging it */}
          <mesh onPointerDown={onDown}>
            <sphereGeometry args={[0.72, 16, 16]} />
            <meshBasicMaterial transparent opacity={0} depthWrite={false} />
          </mesh>

          <BrainMesh clipPlanes={clipPlanes} tint={material.color} opacity={material.opacity} matte={material.matte} glow={material.glow} glowStrength={material.glowStrength} compareEvent={compareEvent} />
          <NeuralWeb positions={data.positions} regions={data.regions} seeds={data.seeds} synapses={data.synapses} clipPlanes={clipPlanes} compareEvent={compareEvent} />

          {/* Invisible hover hit-volumes per lobe / limbic node */}
          {HOTSPOTS.map((h, i) => (
            <mesh
              key={i}
              position={h.pos}
              onPointerOver={(e) => {
                e.stopPropagation();
                setHover(i);
                setHoveredRegion(h.id);
                if (!useSimStore.getState().dragging) document.body.style.cursor = 'grab';
              }}
              onPointerOut={() => {
                setHover(-1);
                setHoveredRegion(0);
                if (!useSimStore.getState().dragging) document.body.style.cursor = 'auto';
              }}
            >
              <sphereGeometry args={[h.r, 12, 12]} />
              <meshBasicMaterial transparent opacity={0} depthWrite={false} colorWrite={false} />
            </mesh>
          ))}

          {/* Floating anatomical name tag for the hovered lobe */}
          <Suspense fallback={null}>
            {hover >= 0 && (
              <Billboard
                position={[
                  HOTSPOTS[hover].pos[0] * 1.18,
                  HOTSPOTS[hover].pos[1] * 1.18 + 0.14,
                  HOTSPOTS[hover].pos[2] * 1.18,
                ]}
              >
                <Text
                  fontSize={0.11}
                  color={HOTSPOTS[hover].color}
                  anchorX="center"
                  anchorY="middle"
                  outlineWidth={0.008}
                  outlineColor="#05060a"
                  letterSpacing={0.02}
                >
                  {HOTSPOTS[hover].name}
                </Text>
              </Billboard>
            )}
          </Suspense>
        </group>
      </group>

      {/* Bold serif exhibit title — positioned directly ABOVE each model,
          sized large for readability. Hidden during focus / compare / tour so
          the labels of scaled-away brains never overlap. */}
      <group ref={labelRef}>
        <Suspense fallback={null}>
          <Billboard position={[0, 1.05, 0]}>
            <Text
              font="https://cdn.jsdelivr.net/fontsource/fonts/playfair-display@latest/latin-700-normal.woff"
              fontSize={0.165}
              color="#0e1620"
              anchorX="center"
              anchorY="middle"
              outlineWidth={0.009}
              outlineColor="#ffffff"
              letterSpacing={0.04}
            >
              {label}
            </Text>
          </Billboard>
        </Suspense>
      </group>
    </group>
  );
}
